import React, { forwardRef } from 'react';
import cn from 'classnames';
import Icon from '@brighthr/component-icon';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
	error?: boolean;
	inputSize?: 'base' | 'xl';
	leftIconName?: string;
	rightIconName?: string;
	unit?: 'days' | 'hrs' | 'mins';
	onClickButton?: () => void;
	buttonAriaLabel?: string;
	label?: string;
	required?: boolean;
};

type OverlayProps = {
	unit?: string;
	iconName?: string;
};

const Overlay = ({ unit, iconName }: OverlayProps) =>
	unit ? (
		<span className="text-gray-400">{unit}</span>
	) : (
		<Icon
			iconName={iconName}
			className="fill-neutral-500 z-LOW"
			size={24}
		/>
	);

const Input = forwardRef<HTMLInputElement, InputProps>(
	(
		{
			error = false,
			disabled,
			inputSize = 'base',
			rightIconName,
			leftIconName,
			unit,
			buttonAriaLabel,
			onClickButton,
			label,
			required = false,
			...rest
		},
		ref
	): React.ReactElement => {
		const hasLeftIcon = !!leftIconName;
		const hasRightIcon = !!rightIconName;
		const hasUnit = !!unit;
		const hasButton = !!onClickButton;
		const hasOverlay = hasRightIcon || hasUnit;

		return (
			<label>
				{(label || required) && (
					<div
						className={cn('flex mb-2 items-center', {
							'justify-between': label && required,
							'justify-end': !label && required,
						})}
					>
						{label && (
							<span className="text-base font-semibold text-neutral-700">
								{label}
							</span>
						)}
						{required && (
							<span className="text-xs font-normal text-accent-600">
								Required
							</span>
						)}
					</div>
				)}

				<div className="relative">
					{hasLeftIcon && (
						<div
							className={cn('absolute z-0 left-2 ', {
								'top-[9px]': inputSize === 'base',
								'top-3': inputSize === 'xl',
							})}
						>
							<Icon
								iconName={leftIconName}
								className="fill-neutral-500"
								size={24}
							/>
						</div>
					)}
					<input
						className={cn(
							'rounded px-3.5 w-full font-semibold text-neutral-700 border-2 outline-none placeholder:font-normal placeholder:text-neutral-400',
							{
								'py-[7px]': inputSize === 'base',
								'py-2.5': inputSize === 'xl',
								'bg-neutral-100 cursor-default text-neutral-300 font-normal':
									disabled,
								'border-neutral-300 focus-visible:border-primary-300':
									!error,
								'border-error-500': error,
								'pl-10': hasLeftIcon,
								'pr-12': hasOverlay,
							}
						)}
						disabled={disabled}
						required={required}
						ref={ref}
						{...rest}
					/>
					{hasOverlay && (
						<div
							className={cn('absolute z-0', {
								'right-4': !hasButton,
								'right-3.5': hasButton,
								'top-[9px]': !hasButton && inputSize === 'base',
								'top-3': !hasButton && inputSize === 'xl',
								'top-[7px]': hasButton && inputSize === 'base',
								'top-2.5': hasButton && inputSize === 'xl',
							})}
						>
							{hasButton ? (
								<button
									type="button"
									aria-label={buttonAriaLabel}
									onClick={onClickButton}
									className="border-2 border-transparent rounded outline-none focus:border-primary-300"
									disabled={disabled}
								>
									<Overlay
										iconName={rightIconName}
										unit={unit}
									/>
								</button>
							) : (
								<Overlay iconName={rightIconName} unit={unit} />
							)}
						</div>
					)}
				</div>
			</label>
		);
	}
);

export default Input;
